// pages/ModuleCard.jsx
import { useParams, useHistory, useLocation } from "react-router";
import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  CircularProgress,
  Chip,
  Stack,
  Paper,
  Avatar,
  IconButton,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Verified from "@mui/icons-material/Verified";
import { ArrowDropDown } from "@mui/icons-material";
import PlayCircleOutlineRounded from "@mui/icons-material/PlayCircleOutlineRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import axios from "axios";
import customAxios from "lib/axios";
import { useTranslation } from "react-i18next";

const BRAND = "#253e66";

const Hero = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  background:
    "linear-gradient(135deg, rgba(37,62,102,0.12) 0%, rgba(37,62,102,0.06) 100%)",
  boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
}));

const StyledAccordion = styled((props) => (
  <Accordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderRadius: 12,
  overflow: "hidden",
  backgroundColor: "#fff",
  "&:before": { display: "none" },
  marginBottom: theme.spacing(1.5),
  "& .MuiAccordionSummary-root": {
    background: "linear-gradient(135deg, #253e66 0%, #1b2f4a 100%)",
    color: "#fff",
  },
  "& .MuiAccordionSummary-content .MuiTypography-root": {
    color: "#fff",
  },
  "& .MuiAccordionDetails-root": {
    backgroundColor: alpha("#253e66", 0.06),
    color: theme.palette.text.primary,
  },
}));

const StyledSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  "& .MuiAccordionSummary-content": { margin: 0, alignItems: "center" },
}));

const StyledDetails = styled(AccordionDetails)(({ theme }) => ({
  background: alpha(BRAND, 0.06),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const LessonCard = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1.25, 1.5),
  borderRadius: 10,
  border: `1px solid ${alpha(BRAND, 0.12)}`,
  backgroundColor: "#fff",
  color: theme.palette.text.primary,
  boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
  transition:
    "transform 120ms ease, box-shadow 120ms ease, background 120ms ease",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
    background: alpha("#27aae1", 0.06),
  },
}));

const ModuleCard = () => {
  const { courseId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation("courses");

  const isEn = String(i18n.resolvedLanguage || i18n.language || "es")
    .toLowerCase()
    .startsWith("en");
  const pick = (es, en) => (isEn ? en ?? es : es);

  const userId = localStorage.getItem("user_id");
  const [modules, setModules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseTitle, setCourseTitle] = useState({ es: null, en: null });
  const [courseDescription, setCourseDescription] = useState({
    es: null,
    en: null,
  });
  const [expanded, setExpanded] = useState(false);

  // Datos del curso (título/desc)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await customAxios.get(`/courses/${courseId}`);
        if (mounted) {
          setCourseTitle({ es: data.title, en: data.titleEN || data.title });
          setCourseDescription({
            es: data.description,
            en: data.descriptionEN || data.description,
          });
        }
      } catch (err) {
        if (mounted && !customAxios.isCancel(err)) setError(err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [courseId]);

  // Módulos y lecciones
  useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();

    const fetchModulesAndLessons = async () => {
      try {
        const { data: modulesData } = await customAxios.get(
          `/modules/${courseId}`,
          { cancelToken: source.token }
        );

        const withLessons = await Promise.all(
          modulesData.map(async (mod) => {
            const { data: lessons } = await customAxios.get(
              `/lessons/${mod.id}`
            );
            const { data: progressData } = await customAxios.get(
              `userCourseProgress/user/module/${mod.id}`
            );
            const lessonsWithProgress = lessons.map((lesson) => ({
              ...lesson,
              completed: progressData.some(
                (p) => p.lessonId === lesson.id && p.completed === 1
              ),
            }));
            const moduleCompleted = lessonsWithProgress.every(
              (l) => l.completed
            );
            return {
              ...mod,
              lessons: lessonsWithProgress,
              completed: moduleCompleted,
            };
          })
        );

        if (isMounted) {
          setModules(withLessons);
          const modIdFromState = location.state?.expandModuleId;
          if (modIdFromState != null) setExpanded(Number(modIdFromState));
        }
      } catch (err) {
        if (!customAxios.isCancel(err) && isMounted) setError(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchModulesAndLessons();
    return () => {
      isMounted = false;
      source.cancel();
    };
  }, [courseId, userId, location.state]);

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{t("error_loading")}</Typography>;

  const totalModulos = modules.length;
  const modulosCompletados = modules.filter((m) => m.completed).length;
  const porcentajeCompletado = Math.round(
    (modulosCompletados / totalModulos) * 100
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <IconButton
          aria-label={t("back")}
          onClick={() => history.push("/myCourses")}
          size="large"
        >
          <ArrowBackIcon />
        </IconButton>

        <Typography variant="h4" textAlign="center" sx={{ flexGrow: 1 }}>
          {pick(courseTitle.es, courseTitle.en)}
        </Typography>

        <Box width={48} />
      </Box>

      {/* Hero con progreso del curso */}
      <Hero>
        <Stack spacing={1}>
          <Typography variant="h6" textAlign="center">
            {pick(courseDescription.es, courseDescription.en)}
          </Typography>
          <Box maxWidth={720} mx="auto" width="100%">
            <Stack direction="row" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2">{t("course_progress")}</Typography>
              <Typography variant="body2">{porcentajeCompletado}%</Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={porcentajeCompletado}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "#e0e0e0",
                "& .MuiLinearProgress-bar": { backgroundColor: "green" },
              }}
            />
          </Box>
        </Stack>
      </Hero>

      {/* Listado de módulos */}
      <Box mt={2} display="flex" flexDirection="column">
        {modules.map((mod) => {
          const done = mod.lessons.filter((l) => l.completed).length;
          const total = mod.lessons.length;
          const modTitle = pick(mod.title, mod.titleEN); // titleEN opcional

          return (
            <StyledAccordion
              key={mod.id}
              expanded={expanded === mod.id}
              onChange={(_, isExp) => setExpanded(isExp ? mod.id : false)}
            >
              <StyledSummary
                expandIcon={<ArrowDropDown sx={{ color: "#fff" }} />}
                aria-controls={`panel-${mod.id}-content`}
                id={`panel-${mod.id}-header`}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  sx={{ width: "100%", color: "#fff" }}
                >
                  <Typography variant="subtitle1">{modTitle}</Typography>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={t("chip_lessons", { done, total })}
                      sx={{
                        bgcolor: alpha("#fff", 0.15),
                        color: "#fff",
                        borderColor: alpha("#fff", 0.35),
                      }}
                      variant="outlined"
                    />
                    {mod.completed && <Verified sx={{ color: "limegreen" }} />}
                  </Stack>
                </Stack>
              </StyledSummary>

              <StyledDetails>
                <Stack spacing={1}>
                  {mod.lessons.map((lesson, idx) => {
                    const lTitle = pick(lesson.title, lesson.titleEN);
                    const lDesc = pick(
                      lesson.description,
                      lesson.descriptionEN
                    );
                    return (
                      <LessonCard
                        key={lesson.id}
                        onClick={() =>
                          history.push(
                            `/myCourses/${courseId}/modules/${mod.id}/lesson/${lesson.id}`
                          )
                        }
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1.5,
                            bgcolor: lesson.completed ? "success.main" : BRAND,
                            color: "#fff",
                            fontSize: 14,
                            fontWeight: 600,
                          }}
                        >
                          {idx + 1}
                        </Avatar>

                        <Box flexGrow={1} minWidth={0}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            noWrap
                          >
                            {lTitle}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {lDesc}
                          </Typography>
                        </Box>

                        <Box ml={1.5} display="flex" alignItems="center">
                          {lesson.completed ? (
                            <CheckCircleRounded color="success" />
                          ) : (
                            <PlayCircleOutlineRounded sx={{ color: BRAND }} />
                          )}
                        </Box>
                      </LessonCard>
                    );
                  })}
                </Stack>
              </StyledDetails>
            </StyledAccordion>
          );
        })}
      </Box>
    </Container>
  );
};

export default ModuleCard;
